import React from "react";
import { Tooltip, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { renderBranchIcon, UserTableProps } from "./UserTableHelpers";
import CommonTable, { HeadCell } from "../../../../../business-types/grocery/components/CommonTable";

// Date formatting function - fixed to show dd-mm-yyyy format
const formatDate = (dateString: string): string => {
  if (!dateString || dateString === "N/A" || dateString.trim() === "") return "N/A";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  } catch (error) {
    return "N/A";
  }
};

const headCells: HeadCell<any>[] = [
  { id: "sno", label: "S.No", disablePadding: true },
  { id: "name", label: "Name" },
  { id: "username", label: "Username" },
  { id: "phone", label: "Phone number" },
  { id: "joined", label: "Date of joining" },
  { id: "leaving", label: "Date of leaving" },
  { id: "status", label: "Status" },
  { id: "branch", label: "Selected branch" },
  { id: "role", label: "Roles" },
  { id: "Actions", label: "Actions", disablePadding: true, align: "center" },
];

const getStatusText = (item: any) => {
  if (item.date_of_leaving && item.status !== "Inactive") return "🟥 Deleted";
  if (item.password && item.status === "Active") return "🟢 Active";
  if (item.status === "Inactive") return "🟠 Inactive";
  return "N/A";
};

// Helper function to check if user is deleted
const isUserDeleted = (item: any): boolean => {
  return !!(item.date_of_leaving && item.status !== "Inactive");
};

const UserTable: React.FC<UserTableProps & { showDeletedUsers?: boolean }> = ({
  filteredData,
  onEditUser,
  onAddCardClick,
  isBranchSelected,
  onDeleteClick,
  showDeletedUsers = false, // Add this prop to detect when viewing deleted users
}) => {
  const tableData = filteredData.map((item, index) => {
    const isDeleted = isUserDeleted(item);

    return {
      id: item._id.$oid,
      sno: index + 1,
      name: item.employee_name || item.name || "",
      username: item.user_name || "N/A",
      phone: item.phone_number,
      joined: item.date_of_joined || "",
      leaving: formatDate(item.date_of_leaving || ""),
      status: getStatusText(item),
      branch: item?.employee?.map(i => i.Branch_name).join(", ") || "N/A",
      role: item.role,
      Actions: (
        <>
          {item.employee_name && (
            <>
              {/* Edit Button - Disabled for deleted users */}
              <Tooltip title={isDeleted ? "Cannot edit deleted user" : "Edit User"} arrow>
                <span> {/* Wrap with span for disabled tooltip */}
                  <IconButton
                    onClick={() => !isDeleted && onEditUser(item._id.$oid)}
                    disabled={isDeleted}
                    sx={{
                      color: isDeleted ? 'gray' : 'black',
                      '&.Mui-disabled': {
                        color: 'gray',
                        opacity: 0.5
                      }
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </span>
              </Tooltip>

              {/* Delete Button - Disabled for deleted users */}
              <Tooltip title={isDeleted ? "User already deleted" : "Delete User"} arrow>
                <span> {/* Wrap with span for disabled tooltip */}
                  <IconButton
                    onClick={() => !isDeleted && onDeleteClick(item._id.$oid, item.employee_name)}
                    disabled={isDeleted}
                    sx={{
                      color: isDeleted ? 'gray' : 'red',
                      '&.Mui-disabled': {
                        color: 'gray',
                        opacity: 0.5
                      }
                    }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
          {/* Branch Icon - Disabled for deleted users */}
          {/* {renderBranchIcon({
            item,
            isBranchSelected,
            onAddCardClick,
            isDeleted // Pass the deleted status to branch icon
          })} */}
        </>
      ),
    };
  });

  return (
    <div className="w-[96%] overflow-x-auto">
      <CommonTable
        title=""
        showToolbar={false}
        data={tableData}
        headCells={headCells}
        selectable={false}
        className="min-w-full"
      />
    </div>
  );
};

export default UserTable;